/**
 * Draft HTML sanitisation.
 *
 * The editor stores rich text as HTML and later writes it back into the DOM, so
 * stored markup is executable unless it is stripped first. The toolbar only ever
 * produces structural tags with no attributes, which lets us use the strongest
 * possible policy: an explicit tag allowlist and **zero permitted attributes**.
 * With no attributes surviving, there is no `onerror`, `src`, `href`, or `style`
 * for an attacker to reach.
 *
 * This runs on the server on every write. The browser additionally rebuilds the
 * tree with `DOMParser` before rendering, so parsing quirks in this tokenizer
 * cannot become a rendering bypass.
 */

/** Exactly the tags the formatting toolbar can produce. */
export const ALLOWED_DRAFT_TAGS: ReadonlySet<string> = new Set([
  "p",
  "br",
  "div",
  "span",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "blockquote",
]);

/**
 * Tags whose *contents* must be discarded, not just the tag itself: their text
 * is machinery (CSS, JS, page titles) rather than the student's words. The
 * form-control tags mirror the web sanitizer's set so a raw API write of a
 * full-page paste stores the same content the browser keeps. Stored markup is
 * re-sanitised by the browser at render, so server-side preservation of text
 * under an unclosed raw-text tag (script/style/textarea) is harmless and
 * intentional.
 */
export const DROP_WITH_CONTENT: ReadonlySet<string> = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "template",
  "noscript",
  "svg",
  "math",
  "link",
  "meta",
  "title",
  "base",
  "textarea",
  "select",
  "option",
  "optgroup",
  "button",
  "label",
  "output",
  "datalist",
  "dialog",
  "details",
  "summary",
  "noframes",
  "frameset",
  "applet",
  "video",
  "audio",
]);

const TAG_PATTERN = /<\/?\s*([a-zA-Z][a-zA-Z0-9-]*)(?:\s[^>]*?)?\/?>/g;
const COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
/** `<?php`, `<!DOCTYPE`, CDATA and other non-tag constructs. */
const ODDITY_PATTERN = /<[!?][^>]*>/g;

/**
 * Strips every tag outside the allowlist and every attribute without exception.
 * Text content is preserved; the contents of script-like elements are removed.
 */
export function sanitizeDraftHtml(html: string): string {
  if (!html) return "";

  let working = html.replace(COMMENT_PATTERN, "").replace(ODDITY_PATTERN, "");

  // Remove dangerous elements together with whatever they contain. A tag is
  // only an element when it is actually closed: `<script>alert(1)</script>`
  // drops the whole block, but a bare `<head>` in prose is stripped as a tag
  // while its text survives — so a student writing "the <head> tag in HTML"
  // is not silently erased from the stored draft. The same applies to a closed
  // `<head>…</head>` pair: its tags are stripped by the rewrite below while the
  // text survives, and a real full-page paste loses nothing because its head
  // contents (title/style/script) are dropped by their own closed-pair nukes.
  for (const tag of DROP_WITH_CONTENT) {
    working = working.replace(
      new RegExp(
        `<\\s*${tag}\\b[\\s\\S]*?<\\s*/\\s*${tag}\\s*>|<\\s*${tag}\\b[^>]*>`,
        "gi",
      ),
      "",
    );
  }

  // Rewrite what remains: allowlisted tags keep their name and nothing else.
  return working.replace(TAG_PATTERN, (match, rawName: string) => {
    const name = rawName.toLowerCase();
    if (!ALLOWED_DRAFT_TAGS.has(name)) return "";

    const isClosing = /^<\s*\//.test(match);
    if (isClosing) return `</${name}>`;
    // Void elements stay self-closed; everything else drops its attributes.
    return name === "br" ? "<br>" : `<${name}>`;
  });
}
